// WhatsApp (Twilio) transport status — shows what is missing in .env, lets managers send a test and see the recent log.
import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Loader2, MessageCircle, RefreshCw, Send, XCircle } from 'lucide-react';
import { apiCall } from './_shared';

const USES = [
  'Welcome (D-1) and thank-you (D+1) guest messages — sent automatically instead of waiting in the queue',
  'Pre-arrival (−3 days) and in-stay pulse messages when the guest has a phone but no e-mail',
  'Arrival-risk re-confirmation requests when no e-mail is on file',
  'Morning briefing text to the numbers set in the briefing settings',
];

const WhatsAppStatusCard = () => {
  const [data, setData] = useState(null);
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try { setData(await apiCall('/api/admin/whatsapp/status')); } catch (e) { setMsg(String(e.message || e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const test = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await apiCall('/api/admin/whatsapp/test', { method: 'POST', body: JSON.stringify({ to }) });
      setMsg(`Test message accepted by Twilio (${r.status}) → ${r.to}`);
      await load();
    } catch (e) { setMsg(String(e.message || e)); } finally { setBusy(false); }
  };

  if (!data) return null;
  const ok = data.configured;
  const logRows = (data.log || []).slice(0, 10).map((l, i) => (
    <li key={`${l.at}-${i}`} className="py-1 flex items-center gap-2 text-xs" data-testid={`whatsapp-log-${i}`}>
      {l.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <XCircle className="w-3.5 h-3.5 text-rose-500" />}
      <span className="text-slate-500">{(l.at || '').slice(0, 16).replace('T', ' ')}</span>
      <span className="font-semibold">{l.kind || 'message'}</span>
      <span className="text-slate-600">→ {l.to}</span>
      {!l.ok && <span className="text-rose-600 truncate">{l.error}</span>}
    </li>
  ));

  return (
    <div className="bg-white rounded-xl border p-4 space-y-3" data-testid="whatsapp-status-card">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="font-bold text-slate-900 flex items-center gap-2"><MessageCircle className="w-4 h-4 text-emerald-600" /> WhatsApp sending (Twilio)</h3>
        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${ok ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`} data-testid="whatsapp-status-chip">
          {ok ? `connected · ${data.sender}` : 'not connected — wa.me links are used instead'}
        </span>
        <button type="button" onClick={load} className="ml-auto text-xs border rounded-lg px-2 py-1 hover:bg-slate-50 inline-flex items-center gap-1" data-testid="whatsapp-refresh"><RefreshCw className="w-3 h-3" /> Refresh</button>
      </div>
      {!ok && (
        <div className="text-sm text-slate-700 bg-amber-50 border border-amber-100 rounded-lg p-3" data-testid="whatsapp-missing">
          <p className="font-semibold mb-1">To activate, add these to <code>backend/.env</code> (from console.twilio.com) and restart the backend:</p>
          <ul className="list-disc ml-5 text-xs">{(data.missing || []).map((k) => <li key={k}><code>{k}</code></li>)}</ul>
          <p className="text-xs text-slate-500 mt-2">Sender example: <code>whatsapp:+14155238886</code> (sandbox) or your approved WhatsApp Business number. Everything below switches on automatically once the keys are present.</p>
        </div>
      )}
      <div>
        <p className="text-xs font-semibold text-slate-600 mb-1">What gets sent via WhatsApp when connected</p>
        <ul className="list-disc ml-5 text-xs text-slate-600 space-y-0.5">{USES.map((u) => <li key={u}>{u}</li>)}</ul>
        <p className="text-[11px] text-slate-400 mt-1">Free-form texts reach guests who wrote to the hotel number in the last 24 h; outside that window Twilio requires an approved template.</p>
      </div>
      <div className="flex items-center gap-2 flex-wrap text-sm">
        <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="+382 67 123 456" className="border rounded px-2 py-1 text-sm w-48" data-testid="whatsapp-test-to" />
        <button type="button" onClick={test} disabled={busy || !to || !ok} className="inline-flex items-center gap-1 bg-emerald-600 text-white rounded-lg px-3 py-1.5 text-sm disabled:opacity-40" data-testid="whatsapp-test-send">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send test
        </button>
        <span className="text-xs text-slate-500">sent {data.sent_total} · failed {data.failed_total}</span>
      </div>
      {msg && <p className="text-xs text-slate-600" data-testid="whatsapp-msg">{msg}</p>}
      {logRows.length > 0 && <ul className="divide-y border-t pt-1" data-testid="whatsapp-log">{logRows}</ul>}
    </div>
  );
};

export default WhatsAppStatusCard;
